/**
 * The live-call feed of a training session: transcript lines, the
 * objection/FAQ entries and the landmine/competitor alerts, all written into
 * the same Redux slices a live sales call uses (see trainingCallActions for
 * knowledge-base cards, liveDetectionActions for Playbook Live's cards).
 *
 * The conversation and alert slices are cleared on mount, on `resetFeed`
 * (a new session on the same page) and on unmount, so a training session
 * never inherits entries from a call or an earlier session, and never leaves
 * its own behind for the live-call page.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { AnyAction } from '@reduxjs/toolkit';

import type {
  CompetitorCardData,
  KbSpeaker,
  LandmineCardData,
  QuestionDetectedData,
  StoredAnswerData,
} from '../services/kbLiveFrames';
import { clearConversation } from '../store/conversationSlice';
import { clearCompetitorAlerts } from '../store/slices/competitorSlice';
import { clearLandmineAlerts } from '../store/slices/landmineSlice';
import {
  competitorAlertActions,
  landmineAlertActions,
  questionDetectedActions,
  storedAnswerActions,
} from '../utils/trainingCallActions';
import { LiveDetectionFrame, liveDetectionActions } from '../utils/liveDetectionActions';
import { useTrainingTranscript } from './useTrainingTranscript';

export interface TrainingCallFeed {
  appendTranscript: (speaker: KbSpeaker, text: string) => void;
  /** Commits the utterance in progress as one transcript line. */
  flushTranscript: () => void;
  questionDetected: (data: QuestionDetectedData) => void;
  storedAnswer: (data: StoredAnswerData) => void;
  landmineAlert: (data: LandmineCardData) => void;
  competitorAlert: (data: CompetitorCardData) => void;
  /** A Playbook Live card frame, in the live detection services' shapes, as on a call. */
  liveDetection: (frame: LiveDetectionFrame, raw: unknown) => void;
  resetFeed: () => void;
}

export function useTrainingCallFeed(): TrainingCallFeed {
  const dispatch = useDispatch();
  const transcript = useTrainingTranscript();

  const dispatchAll = useCallback(
    (actions: AnyAction[]) => actions.forEach((action) => dispatch(action)),
    [dispatch]
  );

  const resetFeed = useCallback(() => {
    transcript.discard();
    dispatch(clearConversation());
    dispatch(clearLandmineAlerts());
    dispatch(clearCompetitorAlerts());
  }, [dispatch, transcript]);

  useEffect(() => {
    resetFeed();
    return resetFeed;
  }, [resetFeed]);

  const now = (): string => new Date().toISOString();

  return useMemo(
    () => ({
      appendTranscript: transcript.appendDelta,
      flushTranscript: transcript.flush,
      questionDetected: (data: QuestionDetectedData) => dispatchAll(questionDetectedActions(data, now())),
      storedAnswer: (data: StoredAnswerData) => dispatchAll(storedAnswerActions(data, now())),
      landmineAlert: (data: LandmineCardData) => dispatchAll(landmineAlertActions(data, now())),
      competitorAlert: (data: CompetitorCardData) => dispatchAll(competitorAlertActions(data, now())),
      liveDetection: (frame: LiveDetectionFrame, raw: unknown) => dispatchAll(liveDetectionActions(frame, raw, now())),
      resetFeed,
    }),
    [dispatchAll, resetFeed, transcript]
  );
}
